/**
 * WinProbabilityBadge — Phase 1 of Match Accuracy roadmap
 *
 * Displays a user's realistic win probability for a grant or corporate scout
 * as a colored 3-bucket badge (🟢 Strong / 🟡 Possible / 🔴 Long Shot).
 *
 * Props:
 *   - winProbability: { pct, bucket, label, reasons } from backend
 *   - size: "sm" | "md" (default "sm")
 *   - showPct: bool — show "32%" inside the badge (default true)
 *   - showReasons: bool — show first reason as tooltip (default true)
 */
import { Target, Lightning, Question } from "@phosphor-icons/react";

const BUCKET_STYLES = {
  strong: {
    bg: "bg-emerald-500/15",
    border: "border-emerald-500/40",
    text: "text-emerald-300",
    dot: "bg-emerald-400",
    icon: Lightning,
  },
  possible: {
    bg: "bg-amber-500/15",
    border: "border-amber-500/40",
    text: "text-amber-300",
    dot: "bg-amber-400",
    icon: Target,
  },
  long_shot: {
    bg: "bg-zinc-700/40",
    border: "border-zinc-600/60",
    text: "text-zinc-400",
    dot: "bg-zinc-500",
    icon: Question,
  },
};

export const WinProbabilityBadge = ({ winProbability, size = "sm", showPct = true, showReasons = true }) => {
  if (!winProbability || !winProbability.bucket) return null;
  const { pct, bucket, label, reasons = [] } = winProbability;
  const styles = BUCKET_STYLES[bucket] || BUCKET_STYLES.long_shot;
  const Icon = styles.icon;
  const padding = size === "md" ? "px-2.5 py-1" : "px-2 py-0.5";
  const textSize = size === "md" ? "text-xs" : "text-[10px]";
  const tooltip = showReasons && reasons.length > 0 ? reasons[0] : label;

  return (
    <span
      className={`inline-flex items-center gap-1 ${padding} ${textSize} font-bold uppercase tracking-wider rounded-sm border ${styles.bg} ${styles.border} ${styles.text}`}
      title={tooltip}
      data-testid={`win-probability-badge-${bucket}`}
    >
      <Icon weight="fill" className="w-3 h-3" />
      {label}
      {showPct && (
        <span className="ml-1 font-mono opacity-80">· {pct}%</span>
      )}
    </span>
  );
};

export default WinProbabilityBadge;
